// A supplier's change request raised on a drawing area, in engineering's queue
// apps/smart-catalog/frontend/src/components/orchestrator/SupplierChangeRequestCard.tsx
//
// The supplier marked an area and said what should change. That was already
// turned into a draft change (fast track), so engineering's job is to review
// the draft: plan it and send it, or withdraw it. The item closes by itself
// once the draft leaves draft. Where the part sits (bill, level) is shown so
// the reviewer knows what it touches before opening anything.

import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Box, Button, Link as MuiLink, Stack, Typography } from '@mui/material';
import React from 'react';
import { useNavigate } from 'react-router-dom';

interface Occurrence {
  bomId: string;
  bomName?: string;
  lineItemId: string;
  depth: number;
}

interface SupplierRequestItem {
  taskPayload?: Record<string, unknown>;
}

export const SupplierChangeRequestCard: React.FC<{ item: SupplierRequestItem }> = ({ item }) => {
  const navigate = useNavigate();
  const tp = (item.taskPayload ?? {}) as {
    partId?: string;
    threadId?: string;
    supplierDisplayName?: string;
    description?: string;
    changeId?: string;
    changeNumber?: string;
    problemReportId?: string;
    problemReportNumber?: string;
    occurrences?: Occurrence[];
  };
  const [title, ...rest] = (tp.description ?? '').split('\n\n');
  const occurrences = tp.occurrences ?? [];

  return (
    <Box sx={{ py: 1.5, px: 1, maxWidth: 820 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>{title || 'Change request'}</Typography>
        <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
          from {tp.supplierDisplayName ?? 'a supplier'}
          {tp.problemReportNumber ? ` · ${tp.problemReportNumber}` : ''}
        </Typography>
      </Stack>

      {rest.length > 0 && (
        <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary', mb: 1, whiteSpace: 'pre-line' }}>{rest.join('\n\n')}</Typography>
      )}

      <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: 'text.secondary', mt: 1, mb: 0.5 }}>WHERE IT IS USED</Typography>
      {occurrences.length === 0 ? (
        <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', mb: 1 }}>On no bill yet.</Typography>
      ) : (
        <Stack spacing={0.25} sx={{ mb: 1.25 }}>
          {[...occurrences]
            .sort((a, b) => b.depth - a.depth)
            .map((o) => (
              <Stack key={`${o.bomId}:${o.lineItemId}`} direction="row" spacing={1} alignItems="baseline">
                <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.78rem', color: 'text.secondary', width: 28 }}>
                  L{o.depth}
                </Typography>
                <MuiLink component="button" underline="hover" onClick={() => navigate(`/bom/${o.bomId}`)} sx={{ fontSize: '0.82rem' }}>
                  {o.bomName ?? 'Bill of materials'}
                </MuiLink>
              </Stack>
            ))}
        </Stack>
      )}

      <Stack direction="row" spacing={1}>
        {tp.changeId && (
          <Button size="small" variant="contained" onClick={() => navigate(`/changes/${tp.changeId}`)} sx={{ textTransform: 'none' }}>
            Review {tp.changeNumber ?? 'the draft change'}
          </Button>
        )}
        {!tp.changeId && tp.problemReportId && (
          <Button
            size="small"
            variant="contained"
            onClick={() => navigate(`/changes/problems?pr=${tp.problemReportId}`)}
            sx={{ textTransform: 'none' }}
          >
            Triage {tp.problemReportNumber ?? 'the report'}
          </Button>
        )}
        {tp.partId && (
          <Button
            size="small"
            color="inherit"
            endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
            onClick={() => navigate(`/parts/${tp.partId}/drawing${tp.threadId ? `?threadId=${tp.threadId}` : ''}`)}
            sx={{ textTransform: 'none' }}
          >
            Open drawing
          </Button>
        )}
      </Stack>
    </Box>
  );
};

export default SupplierChangeRequestCard;
